import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { ApplicationType } from '@/contexts/dynamic-config';
import { createStyles } from '@/contexts/theme-context';
import {
  Employee_Contact_Info_Set_Input,
  EmployeeRole,
  NotificationFieldsFragment,
  UpdateEmployeeContactInfo_NotificationSettingsMutation,
  UpdateEmployeeContactInfo_NotificationSettingsMutationVariables,
} from '@/types';
import { FONTS, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { gql, useMutation } from '@apollo/client';
import {
  ListItem,
  ListItemSeparator,
  ListOption,
} from '@/components/list-item';
import { PaymentsStatus } from '@/enums';
import { SectionList, SectionListData, Text } from 'react-native';
import { Switch } from '@/components/switch';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

export const NOTIFICATION_FIELDS_FRAGMENT = gql`
  fragment NotificationFields on employee_contact_info {
    uuid
    wants_chat_email
    wants_credit_request_email
    wants_order_approval_email
    wants_order_email
    wants_order_tracking_notification
    wants_payment_email
  }
`;

export const NOTIFICATION_SETTINGS_MUTATION = gql`
  mutation UpdateEmployeeContactInfo_NotificationSettings(
    $employeeContactInfoUUID: uuid!
    $set: employee_contact_info_set_input!
  ) {
    update_employee_contact_info_by_pk(
      pk_columns: { uuid: $employeeContactInfoUUID }
      _set: $set
    ) {
      ...NotificationFields
    }
  }

  ${NOTIFICATION_FIELDS_FRAGMENT}
`;

interface Props {
  initialNotificationSettings: NotificationFieldsFragment;
}

interface NotificationListOptionMetadata {
  onToggleChange: (newValue: boolean) => void;
  value: boolean;
}

type NotificationListOption = ListOption<NotificationListOptionMetadata>;

type NotificationListSection = SectionListData<NotificationListOption>;

const EmployeeNotificationsListSeparator: React.FC = () => {
  const [styles] = useStyles();
  return <ListItemSeparator style={styles.listItemSeparator} />;
};

export const EmployeeNotificationsActionSheet: React.FC<Props> = ({
  initialNotificationSettings,
}) => {
  const { showErrorToast } = useToasts();
  const [styles] = useStyles();
  const { selectedRestaurantRelationship } = useUserContext();
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;

  const contactInfoUUID = initialNotificationSettings.uuid;

  const [wantsOrderTrackingNotification, setWantsOrderTrackingNotification] =
    useState(initialNotificationSettings.wants_order_tracking_notification);
  const [wantsChatEmail, setWantsChatEmail] = useState(
    initialNotificationSettings.wants_chat_email,
  );
  const [wantsCreditRequestEmail, setWantsCreditRequestEmail] = useState(
    initialNotificationSettings.wants_credit_request_email,
  );
  const [wantsOrderEmail, setWantsOrderEmail] = useState(
    initialNotificationSettings.wants_order_email,
  );
  const [wantsPaymentEmail, setWantsPaymentEmail] = useState(
    initialNotificationSettings.wants_payment_email,
  );
  const [wantsOrderApprovalEmail, setWantsOrderApprovalEmail] = useState(
    initialNotificationSettings.wants_order_approval_email,
  );

  const [updateNotificationSettings] = useMutation<
    UpdateEmployeeContactInfo_NotificationSettingsMutation,
    UpdateEmployeeContactInfo_NotificationSettingsMutationVariables
  >(NOTIFICATION_SETTINGS_MUTATION);

  const renderRightComponent = ({
    metadata,
    title,
  }: NotificationListOption) => {
    if (!metadata) {
      showErrorToast();
      return null;
    }
    return (
      <Switch
        onValueChange={metadata.onToggleChange}
        testID={`${title}_switch`}
        trackingData={{
          name: `${title}_notification_toggle`,
          properties: {
            value: !metadata.value,
          },
        }}
        value={metadata?.value}
      />
    );
  };

  const onSettingChange = useCallback(
    async (
      value: boolean,
      setToggleValue: (val: boolean) => void,
      setInput: Employee_Contact_Info_Set_Input,
    ) => {
      if (contactInfoUUID) {
        try {
          setToggleValue(value);
          await updateNotificationSettings({
            variables: {
              employeeContactInfoUUID: contactInfoUUID,
              set: setInput,
            },
          });
        } catch {
          showErrorToast();
          setToggleValue(!value);
        }
      } else {
        showErrorToast();
        setToggleValue(!value);
      }
    },
    [contactInfoUUID, showErrorToast, updateNotificationSettings],
  );

  // The order the notifications are pushed to their respective sections is
  // the order they are displayed in the UI.
  const sections = useMemo<NotificationListSection[]>(() => {
    const pushSectionData: NotificationListOption[] = [];
    const emailSectionData: NotificationListOption[] = [];
    if (!isCatalogLimited) {
      const orderTrackingNotification = {
        metadata: {
          onToggleChange: async (val: boolean) => {
            await onSettingChange(val, setWantsOrderTrackingNotification, {
              wants_order_tracking_notification: val,
            });
          },
          value: wantsOrderTrackingNotification,
        },
        title: i18n.t('order_tracking'),
        value: 'wants_order_tracking_notification_toggle',
      };
      pushSectionData.push(orderTrackingNotification);

      const orderEmailNotification = {
        metadata: {
          onToggleChange: async (val: boolean) => {
            await onSettingChange(val, setWantsOrderEmail, {
              wants_order_email: val,
            });
          },
          value: wantsOrderEmail,
        },
        title: i18n.t('order_confirmation'),
        value: 'wants_order_email_toggle',
      };
      emailSectionData.push(orderEmailNotification);
    }

    const pushSection = {
      data: pushSectionData,
      key: i18n.t('push_notifications'),
    };

    const chatEmailNotification = {
      metadata: {
        onToggleChange: async (val: boolean) => {
          await onSettingChange(val, setWantsChatEmail, {
            wants_chat_email: val,
          });
        },
        value: wantsChatEmail,
      },
      title: i18n.t('new_chat_messages'),
      value: 'wants_chat_email_toggle',
    };
    emailSectionData.push(chatEmailNotification);

    if (
      selectedRestaurantRelationship.supplierPaymentsStatus ===
        PaymentsStatus.Enabled &&
      !isCatalogLimited
    ) {
      const paymentEmailNotification = {
        metadata: {
          onToggleChange: async (val: boolean) => {
            await onSettingChange(val, setWantsPaymentEmail, {
              wants_payment_email: val,
            });
          },
          value: wantsPaymentEmail,
        },
        title: i18n.t('payments'),
        value: 'wants_payment_email',
      };
      emailSectionData.push(paymentEmailNotification);
    }

    if (
      selectedRestaurantRelationship.orderReviewRequired &&
      selectedRestaurantRelationship.employeeRole === EmployeeRole.Admin &&
      !isCatalogLimited
    ) {
      const orderApprovalEmailNotification = {
        metadata: {
          onToggleChange: async (val: boolean) => {
            await onSettingChange(val, setWantsOrderApprovalEmail, {
              wants_order_approval_email: val,
            });
          },
          value: wantsOrderApprovalEmail,
        },
        title: i18n.t('order_approval'),
        value: 'wants_order_approval_email',
      };
      emailSectionData.push(orderApprovalEmailNotification);
    }

    if (selectedRestaurantRelationship.showCreditRequest) {
      const creditRequestEmailNotification = {
        metadata: {
          onToggleChange: async (val: boolean) => {
            await onSettingChange(val, setWantsCreditRequestEmail, {
              wants_credit_request_email: val,
            });
          },
          value: wantsCreditRequestEmail,
        },
        title: i18n.t('new_credit_requests'),
        value: 'wants_credit_request_email',
      };
      emailSectionData.push(creditRequestEmailNotification);
    }

    const emailSection = {
      data: emailSectionData,
      key: i18n.t('email_notifications'),
    };
    return [pushSection, emailSection].filter(
      (section) => section.data.length > 0,
    );
  }, [
    isCatalogLimited,
    onSettingChange,
    selectedRestaurantRelationship,
    wantsChatEmail,
    wantsCreditRequestEmail,
    wantsOrderApprovalEmail,
    wantsOrderEmail,
    wantsOrderTrackingNotification,
    wantsPaymentEmail,
  ]);

  return (
    <SectionList
      ItemSeparatorComponent={EmployeeNotificationsListSeparator}
      renderItem={({ item }) => {
        return (
          <ListItem<NotificationListOption>
            item={item}
            listItemStyle={styles.listItemBase}
            renderRightComponent={renderRightComponent}
          />
        );
      }}
      renderSectionHeader={({ section }) => {
        return section.key ? (
          <Text
            style={[
              TYPOGRAPHY.medium,
              FONTS.primarySemiBold,
              styles.listHeaders,
            ]}
          >
            {section.key}
          </Text>
        ) : null;
      }}
      sections={sections}
      stickySectionHeadersEnabled={false}
      style={styles.sectionList}
    />
  );
};

const useStyles = createStyles((theme) => ({
  listHeaders: {
    color: theme.colors.inactiveText,
    marginBottom: 6,
    marginTop: SCREEN_PADDING,
    textTransform: 'uppercase',
  },
  listItemBase: {
    paddingHorizontal: 0,
  },
  listItemSeparator: {
    marginHorizontal: 0,
  },
  sectionList: {
    height: '100%',
  },
}));
